import type { TabLogin, UserModel } from '@/shared/types'
import { STORAGE, getLocalStorage, setLocalStorage } from '@/shared/utils/storage'
import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

// ============ AUTH CONTEXT (Merged with IsLogin) ============
type AuthContextType = {
  // User state
  user: UserModel | null
  setUser: React.Dispatch<React.SetStateAction<UserModel | null>>
  login: (userData: UserModel) => void
  logout: () => void
  // Login status (for session tracking)
  isLogin: string | null
  setIsLogin: (value: string | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserModel | null>(null)
  const [isLogin, setIsLogin] = useState<string | null>(null)

  const login = useCallback((userData: UserModel) => setUser(userData), [])
  const logout = useCallback(() => {
    setUser(null)
    setIsLogin(null)
  }, [])

  const value = useMemo(
    () => ({ user, setUser, login, logout, isLogin, setIsLogin }),
    [user, login, logout, isLogin]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}

// ============ UI CONTEXT (CollapseMenu + ListTab + Theme + Router) ============
import type { ThemeColor, ThemeMode } from '@/components/theme/design-tokens'

// ... (AuthContext remains same)

// ============ UI CONTEXT (CollapseMenu + ListTab + Theme + Router) ============

type UIContextType = {
  // Collapse Menu
  collapseMenu: boolean
  setCollapseMenu: React.Dispatch<React.SetStateAction<boolean>>
  // List Tab
  listTab: TabLogin[]
  setListTab: React.Dispatch<React.SetStateAction<TabLogin[]>>
  // Theme Mode
  theme: ThemeMode
  setTheme: React.Dispatch<React.SetStateAction<ThemeMode>>
  toggleTheme: () => void
  // Theme Color
  themeColor: ThemeColor
  setThemeColor: React.Dispatch<React.SetStateAction<ThemeColor>>
  // Router History (Before)
  routerBefore: string
  setRouterBefore: (value: string) => void
  // Sound Notification (Charger Station)
  isSoundNotif: boolean
  setIsSoundNotif: React.Dispatch<React.SetStateAction<boolean>>
}

const UIContext = createContext<UIContextType | undefined>(undefined)

export const UIProvider = ({ children }: { children: React.ReactNode }) => {
  // State
  const [collapseMenu, setCollapseMenu] = useState<boolean>(false)
  const [listTab, setListTab] = useState<TabLogin[]>([])
  const [theme, setTheme] = useState<ThemeMode>('light')
  const [themeColor, setThemeColor] = useState<ThemeColor>('primary') // Default Red
  const [routerBefore, setRouterBefore] = useState('')
  const [isSoundNotif, setIsSoundNotif] = useState<boolean>(
    () => getLocalStorage<boolean>(STORAGE.IS_SOUND_NOTIF) === true
  )

  // Persist sound notification setting to localStorage
  useEffect(() => {
    setLocalStorage(STORAGE.IS_SOUND_NOTIF, isSoundNotif)
  }, [isSoundNotif])

  // Theme Logic
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  const value = useMemo(
    () => ({
      collapseMenu,
      setCollapseMenu,
      listTab,
      setListTab,
      theme,
      setTheme,
      toggleTheme,
      themeColor,
      setThemeColor,
      routerBefore,
      setRouterBefore,
      isSoundNotif,
      setIsSoundNotif,
    }),
    [collapseMenu, listTab, theme, toggleTheme, themeColor, routerBefore, isSoundNotif]
  )

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}

export const useUI = () => {
  const context = useContext(UIContext)
  if (!context) throw new Error('useUI must be used inside UIProvider')
  return context
}

// Backward compatibility hooks
export const useCollapseMenu = () => {
  const { collapseMenu, setCollapseMenu } = useUI()
  return { collapseMenu, setCollapseMenu }
}

export const useListTab = () => {
  const { listTab, setListTab } = useUI()
  return { listTab, setListTab }
}

export const useTheme = () => {
  const { theme, setTheme, toggleTheme, themeColor, setThemeColor } = useUI()
  return { theme, setTheme, toggleTheme, themeColor, setThemeColor }
}

export const useRouterContext = () => {
  const { routerBefore, setRouterBefore } = useUI()
  return { routerBefore, setRouterBefore }
}

export const useIsLogin = () => {
  const { isLogin, setIsLogin } = useAuth()
  return { isLogin, setIsLogin }
}

export const useSoundNotif = () => {
  const { isSoundNotif, setIsSoundNotif } = useUI()
  return { isSoundNotif, setIsSoundNotif }
}
